'use client';
import { IconButton, Slide, SlideProps } from '@mui/material';
import { AlertProps } from '@mui/material/Alert';
import Snackbar, { SnackbarProps } from '@mui/material/Snackbar';
import React from 'react';
import { COLORS, MOTION } from '../../constants';
import { MaterialIcon } from '../materialIcon';
import { NoticeCard } from '../noticeCard';

const SlideDown = (props: SlideProps) => <Slide {...props} direction="down" />;

/**
 * Renders a floating notice card (same design as customer-mobile toasts) with a close button.
 *
 * @param {boolean} open - The boolean for the Snackbar open prop.
 * @param {AlertProps['severity']} kind - The kind of message for the icon & accent color.
 * @param {string} text - The text to show on the toast.
 * @param {string} subtext - Optional secondary line.
 * @param {Function} close - The function for closing the toast.
 * @return {React.JSX.Element} The rendered Toast component.
 */
export const Toast = ({
  open,
  kind,
  text,
  subtext,
  close,
  ...rest
}: {
  open: boolean;
  kind?: AlertProps['severity'];
  text: string;
  subtext?: string;
  close?: () => void;
} & SnackbarProps) => {
  const handleSnackbarClose = (_event: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    close?.();
  };

  const SnackbarAction = (
    <IconButton
      size="small"
      aria-label="Cerrar"
      onClick={handleSnackbarClose}
      sx={{ height: 44, color: COLORS.text.light3, flexShrink: 0 }}
    >
      <MaterialIcon icon="close" size={20} />
    </IconButton>
  );

  return (
    <Snackbar
      // A new message restarts the auto-hide timer
      key={text}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      open={open}
      autoHideDuration={5000}
      onClose={handleSnackbarClose}
      slots={{ transition: SlideDown }}
      slotProps={{ transition: { timeout: MOTION.short } }}
      // Phone insets only: from 600px MUI centers the toast with left 50% + translateX(-50%), overriding left breaks it
      sx={(theme) => ({
        [theme.breakpoints.down('sm')]: { top: 8, left: 12, right: 12 },
        [theme.breakpoints.up('sm')]: { width: 480 },
      })}
      {...rest}
    >
      <div role={kind === 'error' ? 'alert' : 'status'} style={{ width: '100%' }}>
        <NoticeCard tone={kind ?? 'info'} title={text} body={subtext} action={SnackbarAction} />
      </div>
    </Snackbar>
  );
};
